import React from 'react';

/**
 * Shared, presentational Terms & Conditions body.
 *
 * The same block of content is rendered by both the standalone
 * `/terms` route (for external / shared URLs) and the in-app
 * TermsModal so a single source of truth keeps them from drifting.
 *
 * Purely presentational — accepts no props. All variance (headings,
 * back button, page-vs-modal chrome) is added by the surrounding
 * component.
 */
const Section = ({ title, children }) => (
  <section className="space-y-3">
    <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">{title}</h2>
    <div className="space-y-2 text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed">
      {children}
    </div>
  </section>
);

export const TERMS_LAST_UPDATED = 'July 21, 2026';

const TermsContent = () => (
  <div className="space-y-6" data-testid="terms-content">
    <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
      Welcome to <strong>Pray Now Together</strong> (&ldquo;the Service&rdquo;). By creating an account or
      using any part of this site you agree to the terms below. Please read them
      carefully. If you don&rsquo;t agree, please don&rsquo;t use the Service.
    </p>

    <Section title="1. Who We Are">
      <p>
        Pray Now Together is a community space for sharing prayers and praying alongside
        others. The Service is operated by the Pray Now Together team
        (&ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;). Contact for legal or account
        matters: <a href="mailto:support@praynowtogether.com" className="text-amber-600 dark:text-amber-400 hover:underline">support@praynowtogether.com</a>.
      </p>
    </Section>

    <Section title="2. Eligibility & Accounts">
      <ul className="list-disc pl-5 space-y-1">
        <li>You must be at least 13 years old to create an account.</li>
        <li>You are responsible for keeping your login credentials secure.</li>
        <li>One account per person; automated accounts are not permitted.</li>
      </ul>
    </Section>

    <Section title="3. Your Content">
      <p>
        You retain ownership of the prayers, comments, audio recordings, images, and
        handles you share. By posting, you grant us a non-exclusive, worldwide,
        royalty-free licence to store, display, and transmit that content on the
        Service so other users can view and participate in prayer.
      </p>
      <p>
        You are responsible for the content you submit. Do not upload anything that
        isn&rsquo;t yours to share, or that infringes anyone&rsquo;s rights.
      </p>
    </Section>

    <Section title="4. Sanctuary Community Standards">
      <p>
        Every piece of content is evaluated against a simple rule:
        <em className="mx-1 italic">
          would this be condoned in a normal, mainstream church sanctuary?
        </em>
        Submissions must be family-friendly and reverent. We use automated moderation
        to help enforce this standard. Content that clearly falls outside it — including
        hateful language, profanity, sexually explicit material, graphic violence,
        spam, illegal activity, impersonation, or occult imagery — will be blocked.
      </p>
      <p>
        Reports are welcome via the <strong>Report Misuse</strong> flag on any prayer
        or the general report option in the sidebar. Repeat violations may result in
        content removal and account suspension.
      </p>
    </Section>

    <Section title="5. Prohibited Uses">
      <ul className="list-disc pl-5 space-y-1">
        <li>Harassing, threatening, or doxxing other users.</li>
        <li>Scraping the Service or interfering with its normal operation.</li>
        <li>Attempting to circumvent moderation, rate-limits, or authentication.</li>
        <li>Promoting scams, MLMs, or unrelated commercial solicitations.</li>
        <li>Impersonating real people or public figures.</li>
      </ul>
    </Section>

    <Section title="6. Privacy">
      <p>
        We collect the minimum information needed to run the Service — email, chosen
        handle, prayer content you post, and best-effort location for the &ldquo;Nearby&rdquo;
        feed (derived from your ZIP code or IP). We do not sell personal information.
        A dedicated Privacy Policy will be published alongside this document.
      </p>
    </Section>

    <Section title="7. Third-Party Services">
      <p>
        The Service relies on trusted providers for hosting, object storage, speech
        transcription (OpenAI Whisper), text-to-speech, and content moderation. Their
        own terms may also apply to the data they process on our behalf.
      </p>
    </Section>

    <Section title="8. Availability & Changes">
      <p>
        We may add, modify, or discontinue features at any time. We may also update
        these Terms. When we do, we&rsquo;ll update the &ldquo;Last updated&rdquo; date at the top of
        this page; material changes will be surfaced in-app.
      </p>
    </Section>

    <Section title="9. Disclaimer">
      <p>
        Pray Now Together is a peer community, not a substitute for professional
        medical, mental-health, financial, or legal advice. Prayers and comments here
        are shared &ldquo;as is&rdquo;, without warranty of any kind. If you are in crisis,
        please contact a qualified professional or emergency services in your country.
      </p>
    </Section>

    <Section title="10. Limitation of Liability">
      <p>
        To the fullest extent permitted by law, Pray Now Together and its team shall not
        be liable for indirect, incidental, or consequential damages arising from your
        use of the Service. Total liability, where liability cannot be excluded, is
        limited to the amount you have paid us in the previous twelve months (which,
        for most users, is nothing).
      </p>
    </Section>

    <Section title="11. Termination">
      <p>
        You may close your account at any time by contacting us. We may suspend or
        terminate accounts that violate these Terms or that endanger the community.
      </p>
    </Section>

    <Section title="12. Governing Law">
      <p>
        These Terms are governed by the laws of the jurisdiction in which Pray Now Together
        Now is operated. Nothing in these Terms limits statutory rights you may have
        under the consumer-protection laws of your country.
      </p>
    </Section>

    <Section title="13. Contact">
      <p>
        Questions about these Terms? Reach us at{' '}
        <a href="mailto:support@praynowtogether.com" className="text-amber-600 dark:text-amber-400 hover:underline">
          support@praynowtogether.com
        </a>.
      </p>
    </Section>
  </div>
);

export default TermsContent;
